import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import CustomModal from "../../common/CustomModal";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import AdsAPIs from "../../../apiRoutes/Ads";
import { GET_AD_DELIVERIES_QUERY_KEY, GET_AD_MODERATIONS_QUERY_KEY } from "../../../utils/constants/queryKeys";
import { validateAssetUrl } from "../utils/validateAssetUrl";
const ResubmitAssetModal = ({ open, onClose, delivery, agencyId }) => {
  const [assetUrl, setAssetUrl] = useState("");
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) setAssetUrl("");
  }, [open, delivery?.id]);

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const url = assetUrl.trim();
      if (!url) throw new Error("URL required");
      const { ok, error } = validateAssetUrl(url);
      if (!ok) throw new Error(error || "Invalid video / Drive link");
      if (delivery?.id == null) throw new Error("Missing delivery");
      const payload = {
        resubmit_delivery_id: Number(delivery.id),
        ad_asset_url: url,
        ...(delivery?.topic ? { topic: delivery.topic } : {}),
        ...(delivery?.adset_name ? { adset_name: delivery.adset_name } : {}),
      };
      if (agencyId) payload.delivered_by_agency_id = Number(agencyId);
      return AdsAPIs.createDelivery({ axiosPrivate, payload });
    },
    onSuccess: () => {
      toast.success("Resubmission saved.");
      queryClient.invalidateQueries({ queryKey: GET_AD_DELIVERIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["adDeliveries"] });
      queryClient.invalidateQueries({ queryKey: GET_AD_MODERATIONS_QUERY_KEY });
      onClose?.();
    },
    onError: (e) => {
      toast.error(e?.response?.data?.error_message || e?.message || "Failed to resubmit");
    },
  });

  return (
    <CustomModal open={open} onClose={onClose} title="Resubmit with new asset" isButtonsRequired={false} className="max-w-md">
      <div className="space-y-4 px-1 pb-1">
        {delivery && (
          <p className="text-sm text-muted-foreground">
            Delivery #{delivery.id}
            {delivery.hook_name ? ` · ${delivery.hook_name}` : delivery.hook_number != null ? ` · Hook ${delivery.hook_number}` : ""}
          </p>
        )}
        <div className="space-y-2">
          <Label htmlFor="resubmit-asset-url">New video / Drive URL</Label>
          <Input
            id="resubmit-asset-url"
            value={assetUrl}
            onChange={(e) => setAssetUrl(e.target.value)}
            placeholder="https://…"
            className="w-full"
            autoFocus
            aria-invalid={!validateAssetUrl(assetUrl).ok && !!assetUrl.trim()}
          />
          {assetUrl.trim() && validateAssetUrl(assetUrl).error && (
            <p className="text-xs text-red-600">{validateAssetUrl(assetUrl).error}</p>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="button" className="bg-[#7f7ad5] hover:bg-[#6b66c4]" disabled={isPending || !validateAssetUrl(assetUrl).ok} onClick={() => mutate()}>
            {isPending ? "Submitting…" : "Submit"}
          </Button>
        </div>
      </div>
    </CustomModal>
  );
};

export default ResubmitAssetModal;
